import { useState, useCallback } from "react";
import { z } from "zod";

export interface StepConfig {
  id: string;
  title: string;
  schema: z.ZodSchema;
}

interface UseMultiStepProps {
  steps: StepConfig[];
  initialStep?: number;
  onStepChange?: (step: number) => void;
}

export const useMultiStep = ({
  steps,
  initialStep = 0,
  onStepChange,
}: UseMultiStepProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < steps.length) {
        setCurrentStep(step);
        onStepChange?.(step);
      }
    },
    [steps.length, onStepChange],
  );

  const goToNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    }
  }, [currentStep, steps.length, onStepChange]);

  const goToPrevious = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  }, [currentStep, onStepChange]);

  const markStepAsCompleted = useCallback((step: number) => {
    setCompletedSteps((prev) => new Set(prev).add(step));
  }, []);

  const canGoToNext = currentStep < steps.length - 1;
  const canGoToPrevious = currentStep > 0;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const currentStepConfig = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return {
    currentStep,
    currentStepConfig,
    steps,
    completedSteps,
    canGoToNext,
    canGoToPrevious,
    isLastStep,
    isFirstStep,
    progress,
    goToStep,
    goToNext,
    goToPrevious,
    markStepAsCompleted,
  };
};
